import React, { useState, useMemo, useEffect } from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';
import { PKG_PRODUCTS_SEED_DATA } from '../constants/pkgProductsSeed';
import { PAPA_DEFECTS_MASTER } from '../constants/papaDefectsMaster';
import { pkgOperationsService, type PapaDefectRecord } from '../services/pkgOperationsService';

interface DefectStateItem {
    id: string;
    category: 'A' | 'B' | 'C';
    type: 'Product' | 'Pack';
    name: string;
    description?: string;
    isOthers?: boolean;
    selected: boolean;
    count: number;
    remarks: string;
}

const PAPA: React.FC = () => {
    const { state, dispatch } = usePackagingOperationsContext();

    // ─── 1. Product & Session Setup ───
    const initialLine = state.sessionData.lineNo || 'Line 1';
    const lineProducts = (PKG_PRODUCTS_SEED_DATA || []).filter(p => p.lineName === initialLine);
    const defaultProduct = lineProducts[0] || (PKG_PRODUCTS_SEED_DATA || [])[0];

    const [metadata, setMetadata] = useState({
        productName: defaultProduct?.title || 'CHOCOCHIP COOKIES 75GM[6 KG]',
        sku: defaultProduct?.productCode || '5003146',
        lineNo: initialLine,
        sampleSize: 100,
        batchNo: `B-${new Date().toISOString().slice(5, 10).replace('-', '')}`,
        inspector: state.sessionData.qaExecutive || 'QA Executive'
    });

    const [productSearch, setProductSearch] = useState('');
    const [selectedTab, setSelectedTab] = useState<'ALL' | 'A' | 'B' | 'C'>('ALL');
    const [defectSearch, setDefectSearch] = useState('');

    const filteredProducts = useMemo(() => {
        return (PKG_PRODUCTS_SEED_DATA || []).filter(p => {
            if (!productSearch) return p.lineName === metadata.lineNo;
            const term = productSearch.toLowerCase();
            return p.title.toLowerCase().includes(term) || p.productCode.includes(term);
        });
    }, [metadata.lineNo, productSearch]);

    // ─── 2. Master Sheet Defect State ───
    const [defectStates, setDefectStates] = useState<DefectStateItem[]>(() => {
        return PAPA_DEFECTS_MASTER.map(d => ({
            id: d.id,
            category: d.category,
            type: d.type,
            name: d.name,
            description: d.description,
            isOthers: !!d.isOthers,
            selected: false,
            count: 0,
            remarks: ''
        }));
    });

    // Handle defect checkbox toggle
    const handleToggleDefect = (id: string, checked: boolean) => {
        setDefectStates(prev => prev.map(item => {
            if (item.id !== id) return item;
            return {
                ...item,
                selected: checked,
                count: checked ? (item.count > 0 ? item.count : 1) : 0,
                // keep remarks if already typed, clear if deselected
                remarks: checked ? item.remarks : ''
            };
        }));
    };

    // Handle count change
    const handleCountChange = (id: string, countStr: string) => {
        const val = Math.max(0, Math.min(metadata.sampleSize, parseInt(countStr) || 0));
        setDefectStates(prev => prev.map(item => {
            if (item.id !== id) return item;
            return {
                ...item,
                count: val,
                selected: val > 0 ? true : item.selected
            };
        }));
    };

    // Handle remarks change (crucial for "OTHERS" option)
    const handleRemarksChange = (id: string, remarksVal: string) => {
        setDefectStates(prev => prev.map(item => {
            if (item.id !== id) return item;
            return { ...item, remarks: remarksVal };
        }));
    };

    // Handle product selection change
    const handleProductSelect = (prod: any) => {
        setMetadata(prev => ({
            ...prev,
            productName: prod.title,
            sku: prod.productCode
        }));
        setProductSearch('');
    };

    // ─── 3. Statistical Calculations ───
    const stats = useMemo(() => {
        const sampleSize = Math.max(1, metadata.sampleSize);

        let catACount = 0;
        let catBCount = 0;
        let catCCount = 0;
        let catAActiveCount = 0;
        let catBActiveCount = 0;
        let catCActiveCount = 0;

        defectStates.forEach(d => {
            if (d.selected && d.count > 0) {
                if (d.category === 'A') {
                    catACount += d.count;
                    catAActiveCount++;
                } else if (d.category === 'B') {
                    catBCount += d.count;
                    catBActiveCount++;
                } else if (d.category === 'C') {
                    catCCount += d.count;
                    catCActiveCount++;
                }
            }
        });

        const totalDefects = catACount + catBCount + catCCount;
        const totalDefectPct = (totalDefects / sampleSize) * 100;
        const catAPct = (catACount / sampleSize) * 100;
        const catBPct = (catBCount / sampleSize) * 100;
        const catCPct = (catCCount / sampleSize) * 100;

        const isCriticalDefectPresent = catACount > 0;
        const totalDistinctDefects = catAActiveCount + catBActiveCount + catCActiveCount;

        return {
            sampleSize,
            catACount,
            catBCount,
            catCCount,
            catAPct,
            catBPct,
            catCPct,
            totalDefects,
            totalDefectPct,
            isCriticalDefectPresent,
            totalDistinctDefects
        };
    }, [defectStates, metadata.sampleSize]);

    // ─── 4. Filter Defects by Tab and Search Term ───
    const displayedDefects = useMemo(() => {
        return defectStates.filter(d => {
            if (selectedTab !== 'ALL' && d.category !== selectedTab) return false;
            if (defectSearch) {
                const term = defectSearch.toLowerCase();
                const matchesName = d.name.toLowerCase().includes(term);
                const matchesDesc = (d.description || '').toLowerCase().includes(term);
                const matchesRemarks = d.remarks.toLowerCase().includes(term);
                return matchesName || matchesDesc || matchesRemarks;
            }
            return true;
        });
    }, [defectStates, selectedTab, defectSearch]);

    // Group displayed defects by Category and Type (Product vs Pack)
    const groupedDefects = useMemo(() => {
        const groups: { [key: string]: { Product: DefectStateItem[]; Pack: DefectStateItem[] } } = {
            'A': { Product: [], Pack: [] },
            'B': { Product: [], Pack: [] },
            'C': { Product: [], Pack: [] }
        };

        displayedDefects.forEach(d => {
            if (groups[d.category] && groups[d.category][d.type]) {
                groups[d.category][d.type].push(d);
            }
        });

        return groups;
    }, [displayedDefects]);

    // ─── 5. Sync with Context for Parent Checklist ───
    useEffect(() => {
        const activeDefectRecords: PapaDefectRecord[] = defectStates
            .filter(d => d.selected && d.count > 0)
            .map(d => ({
                id: d.id,
                category: d.category,
                type: d.type,
                name: d.name,
                count: d.count,
                pct: (d.count / Math.max(1, metadata.sampleSize)) * 100,
                remarks: d.remarks || (d.isOthers ? 'Custom unspecified defect' : ''),
                isOthers: d.isOthers
            }));

        dispatch({
            type: 'SET_CHECKLIST_DATA',
            payload: {
                metadata,
                defects: defectStates.map(d => ({
                    ...d,
                    pct: ((d.count / Math.max(1, metadata.sampleSize)) * 100).toFixed(2)
                })),
                activeDefects: activeDefectRecords,
                totalCount: stats.totalDefects,
                totalPct: stats.totalDefectPct.toFixed(2),
                stats
            }
        });
    }, [metadata, defectStates, stats, dispatch]);

    // ─── 6. Direct Save Handler ───
    const handleSavePapa = async () => {
        const tourId = state.sessionData.tourId || `QT-${Date.now()}`;
        const userName = state.sessionData.qaExecutive || null;

        const activeDefectRecords: PapaDefectRecord[] = defectStates
            .filter(d => d.selected && d.count > 0)
            .map(d => ({
                id: d.id,
                category: d.category,
                type: d.type,
                name: d.name,
                count: d.count,
                pct: (d.count / Math.max(1, metadata.sampleSize)) * 100,
                remarks: d.remarks || '',
                isOthers: d.isOthers
            }));

        try {
            await pkgOperationsService.savePapaData({
                tourId,
                productName: metadata.productName,
                sku: metadata.sku,
                sampleSize: metadata.sampleSize,
                defects: activeDefectRecords,
                totalDefectCount: stats.totalDefects,
                totalDefectPct: stats.totalDefectPct,
                categoryACount: stats.catACount,
                categoryBCount: stats.catBCount,
                categoryCCount: stats.catCCount,
                categoryAPct: stats.catAPct,
                categoryBPct: stats.catBPct,
                categoryCPct: stats.catCPct,
                userName
            });

            alert(`PAPA evaluation successfully saved! Total defects: ${stats.totalDefects} (${stats.totalDefectPct.toFixed(2)}%).`);
        } catch (err) {
            console.error('Error saving PAPA data:', err);
            alert('Saved locally for offline synchronization.');
        }
    };

    return (
        <div className="space-y-6">
            {/* ─── Top Header Card ─── */}
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-3 border-b border-slate-100 pb-3">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 block">
                            Packaging Operations Audit
                        </span>
                        <h3 className="text-lg font-extrabold text-slate-800 m-0">
                            PAPA (Product Attributes & Packaging Appearance)
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Standard 100-biscuit sensory & packaging defect inspection with Category A, B, and C classification.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleSavePapa}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm transition"
                        >
                            <span>💾</span> Save PAPA Audit
                        </button>
                    </div>
                </div>

                {/* Metadata Row: Product, SKU, Sample Size, Line */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Product Selection */}
                    <div className="relative">
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Product Variety
                        </label>
                        <input
                            type="text"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={metadata.productName}
                            onChange={e => {
                                setMetadata(prev => ({ ...prev, productName: e.target.value }));
                                setProductSearch(e.target.value);
                            }}
                            placeholder="Search or enter product..."
                        />
                        {productSearch && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                                {filteredProducts.slice(0, 8).map((p, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => handleProductSelect(p)}
                                        className="p-2.5 text-xs hover:bg-indigo-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                                    >
                                        <div className="font-bold text-slate-800">{p.title}</div>
                                        <div className="text-slate-400 font-mono text-[11px]">SKU: {p.productCode} • {p.lineName}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* SKU */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            SKU Code
                        </label>
                        <input
                            type="text"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50 font-mono font-bold text-slate-700"
                            value={metadata.sku}
                            onChange={e => setMetadata(prev => ({ ...prev, sku: e.target.value }))}
                            placeholder="SKU"
                        />
                    </div>

                    {/* Sample Size */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Sample Size (Biscuits)
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                min="10"
                                max="1000"
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={metadata.sampleSize}
                                onChange={e => setMetadata(prev => ({ ...prev, sampleSize: Math.max(1, parseInt(e.target.value) || 100) }))}
                            />
                            <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-semibold pointer-events-none">
                                pcs
                            </span>
                        </div>
                    </div>

                    {/* Batch No & Line */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Line & Batch No
                        </label>
                        <div className="flex gap-2">
                            <span className="px-2.5 py-2 bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 whitespace-nowrap">
                                {metadata.lineNo}
                            </span>
                            <input
                                type="text"
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs bg-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={metadata.batchNo}
                                onChange={e => setMetadata(prev => ({ ...prev, batchNo: e.target.value }))}
                                placeholder="Batch No"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── Defect Scoreboard & Summary KPI Cards ─── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Category A (Critical) */}
                <div className={`p-4 rounded-xl border transition ${
                    stats.catACount > 0
                        ? 'bg-red-50 border-red-300 text-red-900 shadow-sm'
                        : 'bg-white border-slate-200 text-slate-800'
                }`}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-red-600 flex items-center gap-1">
                            <span>🛑</span> Category A (Critical)
                        </span>
                        <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full ${
                            stats.catACount > 0 ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                            {stats.catACount} Defect{stats.catACount !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="text-2xl font-black font-mono">
                        {stats.catAPct.toFixed(2)}%
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2 overflow-hidden">
                        <div
                            className="bg-red-600 h-1.5 transition-all duration-300"
                            style={{ width: `${Math.min(stats.catAPct, 100)}%` }}
                        />
                    </div>
                    <span className="text-[11px] text-slate-500 mt-1 block">
                        Severe food safety / regulatory defects
                    </span>
                </div>

                {/* Category B (Major) */}
                <div className={`p-4 rounded-xl border transition ${
                    stats.catBCount > 0
                        ? 'bg-amber-50 border-amber-300 text-amber-900 shadow-sm'
                        : 'bg-white border-slate-200 text-slate-800'
                }`}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1">
                            <span>⚠️</span> Category B (Major)
                        </span>
                        <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full ${
                            stats.catBCount > 0 ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                            {stats.catBCount} Defect{stats.catBCount !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="text-2xl font-black font-mono">
                        {stats.catBPct.toFixed(2)}%
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2 overflow-hidden">
                        <div
                            className="bg-amber-500 h-1.5 transition-all duration-300"
                            style={{ width: `${Math.min(stats.catBPct, 100)}%` }}
                        />
                    </div>
                    <span className="text-[11px] text-slate-500 mt-1 block">
                        Major quality & packaging deviations
                    </span>
                </div>

                {/* Category C (Minor) */}
                <div className={`p-4 rounded-xl border transition ${
                    stats.catCCount > 0
                        ? 'bg-blue-50 border-blue-200 text-blue-900 shadow-sm'
                        : 'bg-white border-slate-200 text-slate-800'
                }`}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1">
                            <span>ℹ️</span> Category C (Minor)
                        </span>
                        <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full ${
                            stats.catCCount > 0 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                            {stats.catCCount} Defect{stats.catCCount !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="text-2xl font-black font-mono">
                        {stats.catCPct.toFixed(2)}%
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2 overflow-hidden">
                        <div
                            className="bg-blue-600 h-1.5 transition-all duration-300"
                            style={{ width: `${Math.min(stats.catCPct, 100)}%` }}
                        />
                    </div>
                    <span className="text-[11px] text-slate-500 mt-1 block">
                        Aesthetic & minor appearance variations
                    </span>
                </div>

                {/* Overall Combined Rate */}
                <div className={`p-4 rounded-xl border text-slate-800 ${
                    stats.isCriticalDefectPresent
                        ? 'bg-red-50/70 border-red-300'
                        : stats.totalDefects > 0
                        ? 'bg-slate-50 border-slate-200'
                        : 'bg-emerald-50 border-emerald-200'
                }`}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                            Overall Defect Rate
                        </span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            stats.isCriticalDefectPresent
                                ? 'bg-red-600 text-white'
                                : stats.totalDefects > 0
                                ? 'bg-slate-800 text-white'
                                : 'bg-emerald-600 text-white'
                        }`}>
                            {stats.totalDefects} Total
                        </span>
                    </div>
                    <div className="text-2xl font-black font-mono text-slate-900">
                        {stats.totalDefectPct.toFixed(2)}%
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mt-2 overflow-hidden">
                        <div
                            className={`h-1.5 transition-all duration-300 ${
                                stats.isCriticalDefectPresent ? 'bg-red-600' : stats.totalDefects > 0 ? 'bg-indigo-600' : 'bg-emerald-600'
                            }`}
                            style={{ width: `${Math.min(stats.totalDefectPct, 100)}%` }}
                        />
                    </div>
                    <span className="text-[11px] text-slate-500 mt-1 block">
                        {stats.totalDistinctDefects} distinct defect types flagged
                    </span>
                </div>
            </div>

            {/* ─── Defect Filtering & Search Controls ─── */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-wrap justify-between items-center gap-3">
                {/* Tabs */}
                <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
                    {[
                        { key: 'ALL', label: 'All Defects', count: defectStates.length },
                        { key: 'A', label: 'Category A (Critical)', count: PAPA_DEFECTS_MASTER.filter(d => d.category === 'A').length },
                        { key: 'B', label: 'Category B (Major)', count: PAPA_DEFECTS_MASTER.filter(d => d.category === 'B').length },
                        { key: 'C', label: 'Category C (Minor)', count: PAPA_DEFECTS_MASTER.filter(d => d.category === 'C').length }
                    ].map(tab => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setSelectedTab(tab.key as any)}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold transition flex items-center gap-1.5 ${
                                selectedTab === tab.key
                                    ? 'bg-white text-indigo-700 shadow-sm'
                                    : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Search Bar */}
                <div className="relative min-w-[240px]">
                    <input
                        type="text"
                        className="w-full border border-slate-300 rounded-lg pl-8 pr-3 py-1.5 text-xs bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Search defect title or description..."
                        value={defectSearch}
                        onChange={e => setDefectSearch(e.target.value)}
                    />
                    <span className="absolute left-2.5 top-2 text-slate-400 text-xs">🔍</span>
                    {defectSearch && (
                        <button
                            type="button"
                            onClick={() => setDefectSearch('')}
                            className="absolute right-2.5 top-1.5 text-slate-400 hover:text-slate-600 text-xs font-bold"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            {/* ─── Master Sheet of Defects Sections (Rendered Category by Category) ─── */}
            {(['A', 'B', 'C'] as const).map(cat => {
                if (selectedTab !== 'ALL' && selectedTab !== cat) return null;

                const catLabel = cat === 'A' ? 'Critical' : cat === 'B' ? 'Major' : 'Minor';
                const catDefectCount = cat === 'A' ? stats.catACount : cat === 'B' ? stats.catBCount : stats.catCCount;
                const catDefectPct = cat === 'A' ? stats.catAPct : cat === 'B' ? stats.catBPct : stats.catCPct;

                const headerBg = cat === 'A'
                    ? 'bg-red-700 text-white border-red-800'
                    : cat === 'B'
                    ? 'bg-amber-600 text-white border-amber-700'
                    : 'bg-slate-700 text-white border-slate-800';

                const prodList = groupedDefects[cat]?.Product || [];
                const packList = groupedDefects[cat]?.Pack || [];

                if (prodList.length === 0 && packList.length === 0) return null;

                return (
                    <div key={cat} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-0">
                        {/* Category Header */}
                        <div className={`py-3 px-5 flex flex-wrap justify-between items-center gap-3 border-b ${headerBg}`}>
                            <div>
                                <h4 className="text-sm font-black uppercase tracking-wider m-0 flex items-center gap-2">
                                    <span>{cat === 'A' ? '🛑' : cat === 'B' ? '⚠️' : 'ℹ️'}</span>
                                    <span>Category {cat} Defects ({catLabel})</span>
                                </h4>
                                <span className="text-[11px] opacity-90 block mt-0.5">
                                    {cat === 'A'
                                        ? 'Zero-tolerance defects. Any count triggers immediate QA escalation.'
                                        : cat === 'B'
                                        ? 'Functional and dimensional deviations requiring process adjustment.'
                                        : 'Aesthetic surface variations and cosmetic wrapper imperfections.'}
                                </span>
                            </div>

                            <div className="flex items-center gap-3">
                                <span className="bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-bold">
                                    {catDefectCount} Defect{catDefectCount !== 1 ? 's' : ''} ({catDefectPct.toFixed(2)}%)
                                </span>
                            </div>
                        </div>

                        {/* Defect Columns: Product Defects vs Pack Defects */}
                        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
                            {/* Product Defects Column */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between pb-2 border-b border-indigo-100">
                                    <h5 className="text-xs font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1.5 m-0">
                                        <span>🍪</span> Product Defects ({prodList.length})
                                    </h5>
                                    <span className="text-[11px] text-slate-400 font-medium">Bake, Sensory, Cream & Size</span>
                                </div>

                                <div className="space-y-2.5 max-h-[520px] overflow-y-auto pr-1">
                                    {prodList.map(item => (
                                        <DefectRowCard
                                            key={item.id}
                                            item={item}
                                            sampleSize={metadata.sampleSize}
                                            onToggle={handleToggleDefect}
                                            onCountChange={handleCountChange}
                                            onRemarksChange={handleRemarksChange}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Pack Defects Column */}
                            <div className="space-y-3 lg:pl-6 pt-4 lg:pt-0">
                                <div className="flex items-center justify-between pb-2 border-b border-emerald-100">
                                    <h5 className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5 m-0">
                                        <span>📦</span> Pack Defects ({packList.length})
                                    </h5>
                                    <span className="text-[11px] text-slate-400 font-medium">Wrapper, Sealing, Coding & Carton</span>
                                </div>

                                <div className="space-y-2.5 max-h-[520px] overflow-y-auto pr-1">
                                    {packList.map(item => (
                                        <DefectRowCard
                                            key={item.id}
                                            item={item}
                                            sampleSize={metadata.sampleSize}
                                            onToggle={handleToggleDefect}
                                            onCountChange={handleCountChange}
                                            onRemarksChange={handleRemarksChange}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Individual Defect Row Card Component ───
interface DefectRowCardProps {
    item: DefectStateItem;
    sampleSize: number;
    onToggle: (id: string, checked: boolean) => void;
    onCountChange: (id: string, countStr: string) => void;
    onRemarksChange: (id: string, remarksVal: string) => void;
}

const DefectRowCard: React.FC<DefectRowCardProps> = ({
    item,
    sampleSize,
    onToggle,
    onCountChange,
    onRemarksChange
}) => {
    const pct = ((item.count / Math.max(1, sampleSize)) * 100).toFixed(1);

    const isSelected = item.selected;
    const isCatA = item.category === 'A';
    const isOthers = item.isOthers;

    // Highlight card if defect has count > 0
    const cardBg = item.count > 0
        ? (isCatA ? 'bg-red-50/70 border-red-300 ring-1 ring-red-300' : 'bg-amber-50/70 border-amber-300')
        : isSelected
        ? 'bg-slate-50 border-slate-300'
        : 'bg-white border-slate-200 hover:border-slate-300';

    return (
        <div className={`p-3 rounded-lg border transition ${cardBg} space-y-2`}>
            <div className="flex items-start justify-between gap-3">
                {/* Checkbox & Defect Title */}
                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <input
                        type="checkbox"
                        id={`papa-chk-${item.id}`}
                        checked={item.selected}
                        onChange={e => onToggle(item.id, e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                    />
                    <label
                        htmlFor={`papa-chk-${item.id}`}
                        className="cursor-pointer select-none text-xs flex-1 block"
                    >
                        <span className="font-bold text-slate-800 block text-xs leading-snug">
                            {item.name}
                            {isOthers && (
                                <span className="ml-1.5 px-1.5 py-0.2 rounded text-[10px] font-bold bg-purple-100 text-purple-700 uppercase border border-purple-200">
                                    Custom Remark
                                </span>
                            )}
                        </span>
                        {item.description && (
                            <span className="text-[11px] text-slate-500 block leading-tight mt-0.5">
                                {item.description}
                            </span>
                        )}
                    </label>
                </div>

                {/* Defect Count Input & Percentage Pill */}
                <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden bg-white shadow-xs focus-within:ring-2 focus-within:ring-indigo-500">
                        <input
                            type="number"
                            min="0"
                            max={sampleSize}
                            disabled={!item.selected}
                            value={item.selected ? item.count : ''}
                            onChange={e => onCountChange(item.id, e.target.value)}
                            placeholder="0"
                            className="w-14 px-2 py-1 text-center font-mono font-bold text-xs bg-transparent focus:outline-none disabled:bg-slate-100 disabled:text-slate-400"
                        />
                        <span className="px-1.5 py-1 bg-slate-100 text-[10px] font-semibold text-slate-500 border-l border-slate-200">
                            pcs
                        </span>
                    </div>

                    <span className={`px-2 py-1 rounded text-[11px] font-mono font-bold border ${
                        item.count > 0
                            ? (isCatA ? 'bg-red-600 text-white border-red-700' : 'bg-amber-500 text-white border-amber-600')
                            : 'bg-slate-100 text-slate-400 border-slate-200'
                    }`}>
                        {item.count > 0 ? `${pct}%` : '0%'}
                    </span>
                </div>
            </div>

            {/* ─── POINT 2: Text box automatically opens for "OTHERS" or when checked ─── */}
            {(isOthers || item.selected) && (
                <div className={`pt-2 border-t border-slate-200/70 ${isOthers ? 'block' : item.remarks ? 'block' : 'hidden'}`}>
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-bold text-slate-600 shrink-0">
                            {isOthers ? 'Defect Remarks *:' : 'Note:'}
                        </span>
                        <input
                            type="text"
                            value={item.remarks}
                            onChange={e => onRemarksChange(item.id, e.target.value)}
                            placeholder={
                                isOthers
                                    ? 'Describe this specific unlisted defect in detail...'
                                    : 'Optional observation / location note...'
                            }
                            className={`w-full px-2.5 py-1 text-xs rounded border bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                                isOthers && item.selected && !item.remarks
                                    ? 'border-purple-400 bg-purple-50/30'
                                    : 'border-slate-300'
                            }`}
                        />
                    </div>
                </div>
            )}

            {/* Optional Note toggle for non-Others items if not already expanded */}
            {!isOthers && item.selected && !item.remarks && (
                <div className="flex justify-end pt-1">
                    <button
                        type="button"
                        onClick={() => onRemarksChange(item.id, ' ')}
                        className="text-[10px] text-slate-400 hover:text-indigo-600 font-semibold"
                    >
                        + Add note
                    </button>
                </div>
            )}
        </div>
    );
};

export default PAPA;
